<template>
  <div class="admin-container">
    <div class="admin-header">
      <h2>Clubs &amp; venues</h2>
      <div class="admin-nav">
        <router-link to="/admin" class="nav-button">📅 Admin</router-link>
        <router-link to="/admin_comics" class="nav-button">🎭 Comics</router-link>
        <router-link to="/admin_dates" class="nav-button">📊 Events</router-link>
        <router-link to="/admin_rota" class="nav-button">📋 Rota</router-link>
        <button class="logout-button" @click="logout">🚪 Logout</button>
      </div>
    </div>

    <p class="intro">
      Add a night here and it shows on <router-link to="/apply">/apply</router-link> at
      <code>/your-slug</code>. No route edit and no migration per night.
      Archive a club to remove it from the public apply page, block new applications (including via bookmarked links),
      and hide it from the <strong>Events</strong> admin venue filter. Historical events and applications are kept.
    </p>

    <button
      v-if="!showNewNight"
      type="button"
      class="primary-button add-night"
      @click="openNewNight"
    >
      Add a night
    </button>
    <form v-else class="night-form" @submit.prevent="submitNight">
      <h3>{{ editingId ? 'Edit night' : 'New night' }}</h3>
      <label>
        Name
        <input v-model="draft.venue_name" required @input="onNightNameInput" />
      </label>
      <label>
        URL slug
        <input v-model="draft.venue_slug" required @input="slugEdited = true" />
      </label>
      <label>
        Location
        <input v-model="draft.venue_location" placeholder="Harrow, or Online" />
      </label>
      <label>
        Description
        <input v-model="draft.description" />
      </label>
      <label>
        Day
        <select v-model="draft.day_of_week" required>
          <option disabled value="">Pick a day</option>
          <option v-for="day in weekdays" :key="day" :value="day">{{ day }}</option>
        </select>
      </label>
      <label>
        Show time
        <input v-model="draft.show_time" placeholder="7:30 PM" required />
      </label>
      <label>
        Moves to
        <select v-model="draft.new_day_of_week">
          <option value="">No change</option>
          <option v-for="day in weekdays" :key="'next-' + day" :value="day">{{ day }}</option>
        </select>
      </label>
      <label>
        From
        <input v-model="draft.day_change_date" type="date" />
      </label>
      <label class="logo-field">
        <span>
          <input v-model="draft.has_spot_lengths" type="checkbox" />
          Spot lengths (5, 7, 10, headliner)
        </span>
      </label>
      <label>
        Public link text
        <input v-model="draft.address" placeholder="Download Zoom, or the venue address" />
      </label>
      <label>
        Public link
        <input v-model="draft.google_maps_url" placeholder="https://zoom.us/download" />
      </label>
      <label class="logo-field">
        Logo
        <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" @change="onLogoChosen" />
      </label>
      <p class="form-hint">
        Leave spot lengths off when people just take part, with no 5, 7, or 10. The public link can be a maps page, or the Zoom desktop download (https://zoom.us/download). Never the meeting link. That is sent to the acts separately.
      </p>
      <p v-if="formError" class="form-error">{{ formError }}</p>
      <div class="form-actions">
        <button type="submit" class="primary-button" :disabled="savingNight">Save night</button>
        <button type="button" class="secondary-button" @click="closeNewNight">Cancel</button>
      </div>
    </form>

    <div v-if="isLoading" class="loading">Loading venues…</div>
    <div v-else class="clubs-table-wrap">
      <table class="clubs-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Slug</th>
            <th>Location</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="v in venues" :key="v.id">
            <td>
              <router-link :to="'/' + v.venue_slug">{{ v.venue_name }}</router-link>
            </td>
            <td><code>{{ v.venue_slug }}</code></td>
            <td>{{ v.venue_location || '—' }}</td>
            <td>
              <span v-if="v.archived_at" class="badge archived">Archived</span>
              <span v-else class="badge active">Public</span>
            </td>
            <td>
              <button
                type="button"
                class="secondary-button"
                @click="openEditNight(v)"
              >
                Edit
              </button>
              <button
                v-if="!v.archived_at"
                type="button"
                class="secondary-button"
                :disabled="savingId === v.id"
                @click="archiveVenue(v)"
              >
                Archive public face
              </button>
              <button
                v-else
                type="button"
                class="primary-button"
                :disabled="savingId === v.id"
                @click="reinstateVenue(v)"
              >
                Reinstate on home
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
/** @see docs/APP_UI_SPEC.md — F2 (archive / reinstate), F4 (add a night) */
import { supabase } from '../supabaseClient';
import { logoutAdmin } from '../utils/auth.js';
import {
  WEEKDAYS,
  buildNewNightRecord,
  nextDisplayOrder,
  slugFromNightName,
} from '../utils/newNight.js';
import { logoFileError, uploadVenueLogo } from '../utils/venueLogo.js';
import Swal from 'sweetalert2';

function emptyNightDraft() {
  return {
    venue_name: '',
    venue_slug: '',
    venue_location: '',
    description: '',
    day_of_week: '',
    show_time: '',
    new_day_of_week: '',
    day_change_date: '',
    address: '',
    google_maps_url: '',
    has_spot_lengths: true,
  };
}

export default {
  name: 'AdminClubsPage',
  data() {
    return {
      venues: [],
      isLoading: true,
      savingId: null,
      showNewNight: false,
      savingNight: false,
      slugEdited: false,
      editingId: null,
      existingLogoUrl: null,
      logoFile: null,
      formError: '',
      weekdays: WEEKDAYS,
      draft: emptyNightDraft(),
    };
  },
  async created() {
    await this.loadVenues();
  },
  methods: {
    async loadVenues() {
      this.isLoading = true;
      try {
        const { data, error } = await supabase
          .from('venues')
          .select(
            'id, venue_name, venue_slug, venue_location, description, address, google_maps_url, day_of_week, show_time, new_day_of_week, day_change_date, logo_url, has_spot_lengths, archived_at, display_order'
          )
          .order('display_order', { ascending: true });
        if (error) throw error;
        this.venues = data || [];
      } catch (e) {
        console.error(e);
        await Swal.fire({ icon: 'error', title: 'Could not load venues', text: e.message || String(e) });
      } finally {
        this.isLoading = false;
      }
    },

    async archiveVenue(venue) {
      const ok = await Swal.fire({
        icon: 'warning',
        title: `Archive “${venue.venue_name}”?`,
        text: 'It will disappear from the public home page and direct links will stop taking applications. Data is kept.',
        showCancelButton: true,
        confirmButtonText: 'Archive',
        cancelButtonText: 'Cancel',
      });
      if (!ok.isConfirmed) return;
      this.savingId = venue.id;
      try {
        const { error } = await supabase
          .from('venues')
          .update({ archived_at: new Date().toISOString() })
          .eq('id', venue.id);
        if (error) throw error;
        await Swal.fire({ icon: 'success', title: 'Archived', timer: 1500, showConfirmButton: false });
        await this.loadVenues();
      } catch (e) {
        console.error(e);
        await Swal.fire({ icon: 'error', title: 'Update failed', text: e.message || String(e) });
      } finally {
        this.savingId = null;
      }
    },

    async reinstateVenue(venue) {
      const ok = await Swal.fire({
        icon: 'question',
        title: `Show “${venue.venue_name}” on the home page again?`,
        showCancelButton: true,
        confirmButtonText: 'Reinstate',
        cancelButtonText: 'Cancel',
      });
      if (!ok.isConfirmed) return;
      this.savingId = venue.id;
      try {
        const { error } = await supabase.from('venues').update({ archived_at: null }).eq('id', venue.id);
        if (error) throw error;
        await Swal.fire({ icon: 'success', title: 'Reinstated', timer: 1500, showConfirmButton: false });
        await this.loadVenues();
      } catch (e) {
        console.error(e);
        await Swal.fire({ icon: 'error', title: 'Update failed', text: e.message || String(e) });
      } finally {
        this.savingId = null;
      }
    },

    openNewNight() {
      this.draft = emptyNightDraft();
      this.slugEdited = false;
      this.editingId = null;
      this.existingLogoUrl = null;
      this.logoFile = null;
      this.formError = '';
      this.showNewNight = true;
    },

    openEditNight(venue) {
      this.draft = {
        venue_name: venue.venue_name || '',
        venue_slug: venue.venue_slug || '',
        venue_location: venue.venue_location || '',
        description: venue.description || '',
        day_of_week: venue.day_of_week || '',
        show_time: venue.show_time || '',
        new_day_of_week: venue.new_day_of_week || '',
        day_change_date: String(venue.day_change_date || '').slice(0, 10),
        address: venue.address || '',
        google_maps_url: venue.google_maps_url || '',
        has_spot_lengths: venue.has_spot_lengths !== false,
      };
      this.slugEdited = true;
      this.editingId = venue.id;
      this.existingLogoUrl = venue.logo_url || null;
      this.logoFile = null;
      this.formError = '';
      this.showNewNight = true;
    },

    closeNewNight() {
      this.showNewNight = false;
      this.editingId = null;
      this.logoFile = null;
      this.formError = '';
    },

    onLogoChosen(event) {
      const file = event.target.files && event.target.files[0];
      this.logoFile = file || null;
      this.formError = logoFileError(this.logoFile);
    },

    onNightNameInput() {
      if (this.slugEdited) return;
      this.draft.venue_slug = slugFromNightName(this.draft.venue_name);
    },

    async submitNight() {
      this.formError = logoFileError(this.logoFile);
      if (this.formError) return;
      const built = buildNewNightRecord(this.draft, {
        displayOrder: this.editingId ? 0 : nextDisplayOrder(this.venues),
      });
      if (!built.ok) {
        this.formError = built.error;
        return;
      }
      const wasEdit = Boolean(this.editingId);
      this.savingNight = true;
      try {
        const venueId = await this.saveNightRow(built.row);
        if (this.logoFile) {
          const logoUrl = await uploadVenueLogo(supabase, built.row.venue_slug, this.logoFile);
          const { error } = await supabase.from('venues').update({ logo_url: logoUrl }).eq('id', venueId);
          if (error) throw error;
        }
        this.showNewNight = false;
        this.editingId = null;
        this.logoFile = null;
        await this.loadVenues();
        await Swal.fire({
          icon: 'success',
          title: wasEdit ? 'Night updated' : 'Night added',
          text: `Public page: /${built.row.venue_slug}`,
          timer: 2200,
          showConfirmButton: false,
        });
      } catch (e) {
        console.error(e);
        this.formError = e.message || String(e);
      } finally {
        this.savingNight = false;
      }
    },

    async saveNightRow(row) {
      if (!this.editingId) {
        const { data, error } = await supabase.from('venues').insert(row).select('id').single();
        if (error) throw error;
        return data.id;
      }
      const changes = { ...row };
      delete changes.display_order;
      const { error } = await supabase.from('venues').update({
        ...changes,
        logo_url: this.existingLogoUrl,
      }).eq('id', this.editingId);
      if (error) throw error;
      return this.editingId;
    },

    async logout() {
      await logoutAdmin();
      this.$router.push('/');
    },
  },
};
</script>

<style scoped>
.admin-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 20px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  font-family: Arial, sans-serif;
  text-align: left;
}

.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 10px;
}

.admin-header h2 {
  margin: 0;
  color: #2c3e50;
}

.admin-nav {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
}

.nav-button {
  padding: 8px 16px;
  background: #6c757d;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-size: 14px;
}

.nav-button:hover {
  background: #5a6268;
}

.nav-button.router-link-active {
  background: #007bff;
}

.logout-button {
  background-color: #e74c3c;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
}

.intro {
  max-width: 52rem;
  margin: 0 0 1.25rem 0;
  line-height: 1.5;
  color: #444;
}
.clubs-table-wrap {
  overflow-x: auto;
}
.clubs-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  overflow: hidden;
}
.clubs-table th,
.clubs-table td {
  padding: 0.65rem 0.75rem;
  text-align: left;
  border-bottom: 1px solid #eee;
}
.clubs-table th {
  background: #f5f5f5;
  font-weight: 600;
}
.badge {
  display: inline-block;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-size: 0.85rem;
}
.badge.active {
  background: #e8f5e9;
  color: #1b5e20;
}
.badge.archived {
  background: #fce4ec;
  color: #880e4f;
}
.loading {
  padding: 1rem;
}
.secondary-button {
  padding: 0.45rem 0.75rem;
  border: 1px solid #999;
  background: #f5f5f5;
  border-radius: 6px;
  cursor: pointer;
}
.secondary-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.primary-button {
  padding: 0.45rem 0.75rem;
  border: none;
  background: #5c4d7a;
  color: #fff;
  border-radius: 6px;
  cursor: pointer;
}
.primary-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.add-night {
  margin-bottom: 1rem;
}
.night-form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem 1rem;
  margin-bottom: 1.25rem;
  padding: 1rem;
  border: 1px solid #e6e6e6;
  border-radius: 8px;
}
.night-form h3 {
  grid-column: 1 / -1;
  margin: 0;
}
.night-form label {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.9rem;
}
.night-form input,
.night-form select {
  padding: 0.4rem 0.5rem;
  font-size: 1rem;
}
.form-error {
  grid-column: 1 / -1;
  margin: 0;
  color: #842029;
}
.form-actions {
  grid-column: 1 / -1;
  display: flex;
  gap: 0.5rem;
}
.logo-field,
.form-hint {
  grid-column: 1 / -1;
}
.form-hint {
  margin: 0;
  color: #555;
  font-size: 0.9rem;
}
.clubs-table td .secondary-button {
  margin-right: 0.35rem;
}
</style>
